import { useState } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import {
  useGlobalUser,
  useNoAccountModal,
  useSegment,
} from '@sparknz/journeys-common';
import { Impersonate } from '@sparknz/online-web-shadow-mode';
import { useSnackbar } from '@sparknz/spark-pattern-library';

import { useAddBundlesAccountsQuery } from '__generated__/useAddBundlesAccountsQuery.graphql';
import {
  useAddBundlesMutation,
  useAddBundlesMutation$variables,
} from '__generated__/useAddBundlesMutation.graphql';
import { isShadowModeEnabled } from 'components/shadowMode/shadowMode.utils';
import { MobilePlansDetails } from 'components/shop/MobilePlans/typings/plans';
import { trackSuccessfulAddToCartEvent } from 'components/shop/MobilePlans/utils';
import {
  ADDONS_PAGE,
  ADDONS_SME_PAGE,
  CART_PAGE,
  CART_SME_PAGE,
} from 'shared/constants/path.constants';
import useGuestLogin from 'shared/hooks/useGuestLogin';
import { trackFailedLoginEvent } from 'shared/utils/loginTracking.utils';

const accountListQuery = graphql`
  query useAddBundlesAccountsQuery {
    me {
      accountList {
        accountId
        segment
      }
    }
  }
`;

const addBundlesMutation = graphql`
  mutation useAddBundlesMutation($input: AddBundlesInput!) {
    addBundles(input: $input) {
      code
      message
      success
      bundles {
        bundleId
        items {
          itemId
        }
        isAffected
      }
    }
  }
`;

interface AddBundlesParameter {
  bundles: useAddBundlesMutation$variables['input']['bundles'];
  planCardsProducts: MobilePlansDetails;
  bypassAddonsPage?: boolean;
}

export default function useAddBundles() {
  const [isLoading, setLoading] = useState(false);
  const [mutateAddBundles] =
    useMutation<useAddBundlesMutation>(addBundlesMutation);
  const { channel, isBusiness, segment } = useSegment();
  const { showSnackbar } = useSnackbar();
  const {
    authenticatedInfo: { isLoggedIn, hasSparkId },
    refetchUser,
  } = useGlobalUser();
  const { tryGuestLogin } = useGuestLogin();

  const accountListResponse = useLazyLoadQuery<useAddBundlesAccountsQuery>(
    accountListQuery,
    {},
  );

  const { openNoAccountModal } = useNoAccountModal();
  async function addToCart({
    bundles,
    planCardsProducts: allPlanCardsProducts,
    bypassAddonsPage,
  }: AddBundlesParameter) {
    mutateAddBundles({
      variables: {
        input: {
          bundles,
          channel,
          processContext: 'NEW_OFFER',
          agentDetails: isShadowModeEnabled
            ? Impersonate.getAgentDetailsCookie()
            : null,
        },
      },
      onCompleted(data) {
        setLoading(false);

        const { code, message, success, bundles: newBundles } = data.addBundles;

        const affectedBundles =
          newBundles?.filter((bundle) => bundle.isAffected) || [];

        if (!success) {
          trackFailedLoginEvent({
            errorMessage: message,
            internalErrorCode: code.toString(),
            internalErrorMessage: message,
          });
          showSnackbar({
            text: `${message}`,
            variant: 'error',
          });
          return;
        }

        const addedOfferIds = bundles.map((bundle) => bundle.items[0].offerId);

        const relevantPlanCardProducts = addedOfferIds.map((addedOfferId) => {
          return allPlanCardsProducts.find(
            (planCardsProduct) =>
              planCardsProduct.planDetails.planId === addedOfferId,
          );
        });

        // This is temporory fix, not getting all plan card product details for different planIds
        const filteredPlanCardProducts =
          relevantPlanCardProducts.filter(Boolean);

        trackSuccessfulAddToCartEvent({
          isBusiness,
          planCardsProducts: filteredPlanCardProducts as MobilePlansDetails,
        });

        if (affectedBundles.length > 1 || bypassAddonsPage) {
          const cartPageURL = isBusiness ? CART_SME_PAGE : CART_PAGE;
          window.location.assign(`${cartPageURL}/`);
          return;
        }

        const { bundleId, items } = affectedBundles[0];
        const { itemId } = items[0];

        const addonsPageURL = isBusiness ? ADDONS_SME_PAGE : ADDONS_PAGE;
        window.location.assign(
          `${addonsPageURL}/?bundleId=${bundleId}&itemIds=${itemId}`,
        );
      },
      onError(error: Error) {
        setLoading(false);

        trackFailedLoginEvent({
          errorMessage: error.message,
          internalErrorCode: '5000',
          internalErrorMessage: 'Something went wrong',
        });
        showSnackbar({
          text: `${error.message}`,
          variant: 'error',
        });
      },
    });
  }

  async function addBundles(parameter: AddBundlesParameter): Promise<void> {
    setLoading(true);
    if (!isLoggedIn) {
      const { success } = await tryGuestLogin();
      if (!success) {
        setLoading(false);
        return;
      }
      refetchUser();
    }

    if (hasSparkId) {
      const hasSegmentAccount = accountListResponse?.me?.accountList?.some(
        (account) => account.segment === segment,
      );

      if (!hasSegmentAccount) {
        openNoAccountModal({
          onCheckoutWithNewAccount: async () => {
            const { success } = await tryGuestLogin();
            if (!success) {
              setLoading(false);
              return;
            }
            refetchUser();
            addToCart(parameter);
          },
          onClose: () => {
            setLoading(false);
          },
          onContinue: () => {
            addToCart(parameter);
          },
        });
        return;
      }
    }
    await addToCart(parameter);
  }

  return {
    addBundles,
    isAddingBundles: isLoading,
  };
}
